import {
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  Download,
  KeyRound,
  Link2,
  PackageCheck,
  Plug,
  Settings2,
  ShoppingCart,
  Store,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { StructuredData } from "@/components/StructuredData";
import { SiteLayout } from "@/components/site/SiteLayout";
import { buildPublicAppUrl } from "@/lib/appConfig";

const installSteps = [
  {
    icon: Download,
    title: "Install the plugin",
    body: "Upload the Splitkart WooCommerce connector ZIP from WordPress Admin > Plugins > Add New > Upload Plugin, then activate it.",
  },
  {
    icon: KeyRound,
    title: "Paste your Merchant Token",
    body: "Open WooCommerce > Splitkart and paste the WooCommerce Merchant Token from your Splitkart merchant dashboard.",
  },
  {
    icon: Settings2,
    title: "Choose storefront placement",
    body: "Enable the split button on product pages, cart pages, or both. Splitkart also supports compatible mini-cart layouts.",
  },
  {
    icon: BadgeCheck,
    title: "Test the split flow",
    body: "Start a split from your store, pay the organizer share, invite friends, and confirm the final WooCommerce order is created after full payment.",
  },
];

const howItWorks = [
  "The shopper clicks Split with Friends below Add to Cart, in the cart, or from a supported mini-cart.",
  "Splitkart opens a hosted split-payment setup page with the WooCommerce cart items, total, store currency, and merchant token.",
  "The organizer adds delivery details and participants, then pays their own share through native WooCommerce checkout.",
  "Participants receive secure contribution links and pay their shares through WooCommerce checkout.",
  "Once all shares are paid, Splitkart creates one final combined WooCommerce order for fulfillment.",
];

const features = [
  "Product page, cart page, and mini-cart Split with Friends buttons",
  "Native WooCommerce checkout for organizer and participant payments",
  "Final combined order after all contribution shares are paid",
  "Draft share orders for individual paid contributions",
  "Store currency, cart items, quantities, product names, and images passed to Splitkart",
  "Merchant-managed button color, button text, and help link/bubble preferences",
  "Signed REST callbacks and merchant-token validation",
  "WooCommerce HPOS compatibility declaration",
];

const faqs = [
  {
    q: "Do WooCommerce merchants need Shopify credentials?",
    a: "No. WooCommerce stores connect with a WooCommerce Merchant Token and use native WooCommerce checkout. Shopify credentials are not required.",
  },
  {
    q: "Where does the Split with Friends button appear?",
    a: "The connector can show the button below Add to Cart on single product pages, on the cart page, and inside supported mini-cart layouts.",
  },
  {
    q: "Does Splitkart replace WooCommerce checkout?",
    a: "No. Splitkart coordinates the split payment flow, while organizer and participant payments continue through WooCommerce checkout.",
  },
  {
    q: "When is the final WooCommerce order created?",
    a: "The final shippable order is created after every participant share has been paid. Individual share orders are kept as draft contribution records.",
  },
  {
    q: "What do I need before setup?",
    a: "You need WooCommerce installed, the Splitkart connector plugin, and a WooCommerce Merchant Token from your Splitkart merchant account.",
  },
];

const pluginSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "SoftwareApplication",
      "@id": buildPublicAppUrl("/woocommerce#woocommerce-plugin"),
      name: "Splitkart for WooCommerce",
      applicationCategory: "BusinessApplication",
      applicationSubCategory: "WooCommerce split payments and group gifting plugin",
      operatingSystem: "WordPress, WooCommerce",
      url: buildPublicAppUrl("/woocommerce"),
      description:
        "Splitkart for WooCommerce adds group gifting and split payment buttons to WooCommerce product, cart, and mini-cart experiences.",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
      },
    },
    {
      "@type": "WebPage",
      "@id": buildPublicAppUrl("/woocommerce#webpage"),
      name: "Splitkart for WooCommerce",
      url: buildPublicAppUrl("/woocommerce"),
      description:
        "Install and set up the Splitkart WooCommerce connector for group gifting and split payments.",
      about: {
        "@id": buildPublicAppUrl("/woocommerce#woocommerce-plugin"),
      },
    },
    {
      "@type": "FAQPage",
      "@id": buildPublicAppUrl("/woocommerce#faq"),
      mainEntity: faqs.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a,
        },
      })),
    },
  ],
};

function WooCommercePlugin() {
  return (
    <SiteLayout>
      <SEO
        title="Splitkart WooCommerce Plugin"
        description="Install the Splitkart WooCommerce connector to add Split with Friends buttons, native WooCommerce split checkout, and final combined orders after all shares are paid."
        url="/woocommerce"
      />
      <StructuredData data={pluginSchema} />

      <section className="section bg-gradient-hero">
        <div className="container-tight">
          <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div>
              <span className="eyebrow">
                <span className="h-1.5 w-1.5 rounded-full bg-success" />
                WooCommerce connector
              </span>
              <h1 className="mt-5 text-balance text-4xl font-bold md:text-6xl">
                Add group gifting and split payments to your
                <span className="text-primary"> WooCommerce store.</span>
              </h1>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-muted-foreground">
                Splitkart for WooCommerce lets customers split a product or cart with friends while your store keeps native WooCommerce checkout, store currency, and final order handling.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Button asChild className="h-12 rounded-lg px-5 text-sm font-semibold">
                  <Link to="/merchant-login">
                    Get Merchant Token
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="h-12 rounded-lg px-5 text-sm font-semibold">
                  <Link to="/contact">Talk to Support</Link>
                </Button>
              </div>
              <div className="mt-5 flex flex-wrap gap-3 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-2 rounded-full bg-background/80 px-3 py-1">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  Native WooCommerce checkout
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-background/80 px-3 py-1">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  HPOS compatible
                </span>
              </div>
            </div>

            <div className="rounded-3xl border border-border bg-card p-6 shadow-elevated">
              <div className="rounded-2xl bg-primary/5 p-5">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-primary p-3 text-primary-foreground">
                    <Plug className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-primary">Plugin setup</p>
                    <h2 className="text-2xl font-bold">WooCommerce &gt; Splitkart</h2>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  {["Enable Splitkart", "Paste Merchant Token", "Set button position", "Start test split"].map((item) => (
                    <div key={item} className="flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-3">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <span className="text-sm font-semibold">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-tight">
          <div className="mx-auto max-w-2xl text-center">
            <span className="eyebrow">How it works</span>
            <h2 className="mt-4 text-3xl font-bold md:text-5xl">A WooCommerce-native split payment flow.</h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Splitkart coordinates the group payment, but WooCommerce remains the checkout and order system for your store.
            </p>
          </div>

          <div className="mt-10 grid gap-4">
            {howItWorks.map((item, index) => (
              <div key={item} className="grid gap-4 rounded-2xl border border-border bg-card p-5 shadow-soft md:grid-cols-[auto_1fr] md:items-center">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                  {index + 1}
                </div>
                <p className="text-base leading-7 text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-surface">
        <div className="container-tight">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div>
              <span className="eyebrow">Install and setup</span>
              <h2 className="mt-4 text-3xl font-bold md:text-5xl">Launch the connector in a few steps.</h2>
              <p className="mt-4 text-lg leading-8 text-muted-foreground">
                After activation, the only required Splitkart credential is the WooCommerce Merchant Token from your Splitkart dashboard.
              </p>
            </div>

            <div className="grid gap-4">
              {installSteps.map((step) => {
                const Icon = step.icon;

                return (
                  <div key={step.title} className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                    <div className="flex items-start gap-4">
                      <div className="rounded-2xl bg-primary/10 p-3 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold">{step.title}</h3>
                        <p className="mt-2 leading-7 text-muted-foreground">{step.body}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-tight">
          <div className="grid gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-start">
            <div>
              <span className="eyebrow">What the plugin includes</span>
              <h2 className="mt-4 text-3xl font-bold md:text-5xl">Built for product pages, carts, mini-carts, and final order sync.</h2>
              <div className="mt-8 grid gap-3 md:grid-cols-2">
                {features.map((feature) => (
                  <div key={feature} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-soft">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                    <p className="text-sm leading-6 text-muted-foreground">{feature}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-border bg-card p-8 shadow-soft">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-primary/10 p-3 text-primary">
                  <ShoppingCart className="h-5 w-5" />
                </div>
                <h3 className="text-2xl font-bold">Button placements</h3>
              </div>
              <div className="mt-6 space-y-4">
                <div className="rounded-2xl bg-surface p-5">
                  <p className="font-semibold">Single product page</p>
                  <p className="mt-2 text-sm text-muted-foreground">Shows below the Add to Cart button so shoppers can start a split before cart checkout.</p>
                </div>
                <div className="rounded-2xl bg-surface p-5">
                  <p className="font-semibold">Cart page</p>
                  <p className="mt-2 text-sm text-muted-foreground">Uses the full cart payload for multi-product split purchases.</p>
                </div>
                <div className="rounded-2xl bg-surface p-5">
                  <p className="font-semibold">Mini-cart</p>
                  <p className="mt-2 text-sm text-muted-foreground">Adds a quick split entry point for compatible classic and block mini-cart layouts.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-surface">
        <div className="container-tight max-w-4xl">
          <div className="mx-auto max-w-2xl text-center">
            <span className="eyebrow">FAQs</span>
            <h2 className="mt-4 text-3xl font-bold md:text-5xl">WooCommerce plugin questions.</h2>
          </div>

          <div className="mt-10 rounded-3xl border border-border bg-card p-2 shadow-soft">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((item, index) => (
                <AccordionItem key={item.q} value={`faq-${index}`} className="border-border px-5">
                  <AccordionTrigger className="text-left text-base font-semibold hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-muted-foreground">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-tight">
          <div className="rounded-3xl bg-foreground p-8 text-background shadow-elevated md:p-10">
            <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <div className="flex items-center gap-3">
                  <Store className="h-6 w-6 text-primary" />
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-background/70">Ready to connect WooCommerce?</p>
                </div>
                <h2 className="mt-4 text-3xl font-bold md:text-4xl">Create a merchant account and copy your WooCommerce Merchant Token.</h2>
                <p className="mt-3 text-background/70">
                  The token links your WooCommerce store to Splitkart without Shopify credentials.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row md:flex-col">
                <Button asChild className="h-12 rounded-lg px-5 text-sm font-semibold">
                  <Link to="/merchant-login">
                    Get Merchant Token
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="secondary" className="h-12 rounded-lg px-5 text-sm font-semibold">
                  <Link to="/how-it-works">
                    See Customer Journey
                    <Link2 className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PackageCheck className="sr-only" aria-hidden />
    </SiteLayout>
  );
}

export default WooCommercePlugin;
